import { useId } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';

import { PatientOrderingProduct } from '@customTypes';

import { getProductTestDisplayName } from '../utils/productDisplayName';

const APOE_PRETEST_GUIDE = {
  title: 'What to know before APOE testing for Alzheimer’s risk',
  href: 'https://myome.com/pdfs/m-154-atlas-apoe-patient-pre-test-overview-v1.pdf',
};

interface ProductConfirmationModalProps {
  product: PatientOrderingProduct;
  requiredProducts: PatientOrderingProduct[];
  onConfirm: () => void;
  onCancel: () => void;
}

const isApoeProduct = (product: PatientOrderingProduct): boolean =>
  /apoe/i.test(`${product.name} ${product.product_test_name ?? ''}`);

export const ProductConfirmationModal = ({
  product,
  requiredProducts,
  onConfirm,
  onCancel,
}: ProductConfirmationModalProps) => {
  const productName = getProductTestDisplayName(product);
  const modalTitleId = useId();

  return (
    <Modal centered show size='lg' onHide={onCancel} aria-labelledby={modalTitleId}>
      <Modal.Header>
        <Modal.Title id={modalTitleId}>Confirm Test Selection</Modal.Title>
      </Modal.Header>

      <Modal.Body className='d-flex flex-column gap-3'>
        <p className='lead'>
          {product.confirmation_text || `Are you sure you want to add ${productName} to your order?`}
        </p>

        {isApoeProduct(product) && (
          <a href={APOE_PRETEST_GUIDE.href} target='_blank' rel='noopener noreferrer'>
            {APOE_PRETEST_GUIDE.title}
          </a>
        )}

        {requiredProducts.length > 0 && (
          <Alert variant='warning' className='d-flex flex-column gap-2'>
            <p>This test will be removed if none of the following tests are also included in your order:</p>

            <ul className='mb-0'>
              {requiredProducts.map(requiredProduct => (
                <li key={requiredProduct.product_id}>{getProductTestDisplayName(requiredProduct)}</li>
              ))}
            </ul>
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button type='button' onClick={onCancel} variant='secondary'>
          Cancel
        </Button>

        <Button type='button' onClick={onConfirm} aria-label={`Confirm adding ${productName} to your order`}>
          Add Test
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
